import React, { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TOKEN_KEY = "ct_login_token";
const USER_KEY = "ct_login_user";
const TWO_FA_TOKEN_KEY = "ct_2fa_token";
const TWO_FA_USER_KEY = "ct_2fa_user";
const EMAIL_OTP_TOKEN_KEY = "ct_email_otp_token";

function defaultPathForRole(role) {
  return role === "nexo" ? "/nexo" : role === "secret" ? "/x-portal" : "/";
}

export default function TwoFactorSetup() {
  const navigate = useNavigate();
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [secret, setSecret] = useState("");

  const tempToken = localStorage.getItem(TWO_FA_TOKEN_KEY);
  const tempUser = JSON.parse(localStorage.getItem(TWO_FA_USER_KEY) || "null");

  const [form, setForm] = useState({
    fullName: tempUser?.fullName || "",
    email: tempUser?.email || "",
    phone: tempUser?.phone || "",
    totpCode: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    if (!tempToken) {
      navigate("/login", { replace: true });
      return;
    }

    const loadSetup = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await fetch("/api/2fa/setup", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tempToken}`,
          },
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data.success) {
          throw new Error(data.error || "2FA setup failed");
        }

        setQrDataUrl(data.qrDataUrl);
        setSecret(data.secret || "");
      } catch (err) {
        setError(err.message || "2FA setup failed");
      } finally {
        setLoading(false);
      }
    };

    loadSetup();
  }, [tempToken, navigate]);

  const submitSetup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/2fa/verify-setup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tempToken}`,
        },
        body: JSON.stringify(form),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data.error || "2FA verification failed");
      }

      if (data.requiresEmailOtp) {
        localStorage.setItem(EMAIL_OTP_TOKEN_KEY, data.emailOtpToken);
        localStorage.setItem(TWO_FA_USER_KEY, JSON.stringify(data.user));
        navigate("/email-otp", { replace: true });
        return;
      }

      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      localStorage.removeItem(TWO_FA_TOKEN_KEY);
      localStorage.removeItem(TWO_FA_USER_KEY);

      if (["admin", "secret"].includes(data.user?.role)) {
        localStorage.setItem("ct_admin_auth", "true");
      } else {
        localStorage.removeItem("ct_admin_auth");
      }

      navigate(defaultPathForRole(data.user?.role), { replace: true });
    } catch (err) {
      setError(err.message || "2FA verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111827] text-white flex items-center justify-center px-4">
      <form onSubmit={submitSetup} className="w-full max-w-[520px] bg-[#1f2937] rounded-xl px-8 py-8 shadow-2xl">
        <div className="flex flex-col items-center mb-6">
          <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center mb-4">
            <ShieldCheck size={30} />
          </div>
          <h1 className="text-2xl font-bold">Two Factor Setup</h1>
          <p className="text-gray-300 text-sm mt-2 text-center">
            Scan this QR code in Google Authenticator, then enter your details and OTP.
          </p>
        </div>

        {qrDataUrl && (
          <div className="flex justify-center mb-5">
            <img src={qrDataUrl} alt="Authenticator QR Code" className="bg-white p-3 rounded-lg w-48 h-48" />
          </div>
        )}

        {secret && (
          <div className="text-xs text-gray-400 text-center mb-5 break-all">
            Manual key: <span className="text-blue-300">{secret}</span>
          </div>
        )}

        <input
          className="w-full bg-[#374151] border border-[#4b5563] rounded-lg py-3 px-3 mb-3"
          placeholder="Full Name"
          value={form.fullName}
          onChange={(e) => setForm(prev => ({ ...prev, fullName: e.target.value }))}
        />

        <input
          className="w-full bg-[#374151] border border-[#4b5563] rounded-lg py-3 px-3 mb-3 opacity-70"
          placeholder="Business Email"
          value={form.email}
          disabled
        />

        <input
          className="w-full bg-[#374151] border border-[#4b5563] rounded-lg py-3 px-3 mb-3"
          placeholder="Phone Number"
          value={form.phone}
          onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
        />

        <input
          className="w-full bg-[#374151] border border-[#4b5563] rounded-lg py-3 px-3 mb-4"
          placeholder="6-digit Authenticator OTP"
          value={form.totpCode}
          onChange={(e) => setForm(prev => ({ ...prev, totpCode: e.target.value }))}
        />

        {error && <div className="text-red-300 text-sm mb-4">{error}</div>}

        <button disabled={loading} className="w-full bg-blue-500 hover:bg-blue-400 rounded py-3 font-semibold disabled:opacity-60">
          {loading ? "Verifying..." : "Verify & Continue"}
        </button>
      </form>
    </div>
  );
}
